'use client';

import React, { useMemo } from 'react';

import { useAuthContext } from '@/components/Providers/AuthProvider';
import { useRoqLogicLibrary } from '@/components/Providers/RoqLogicLibraryProvider';
import DashboardAnnouncementIcon from '@/components/svg/DashboardAnnouncementIcon';
import DashboardFileIcon from '@/components/svg/DashboardFileIcon';
import DashboardHelpIcon from '@/components/svg/DashboardHelpIcon';
import DashboardIcon from '@/components/svg/DashboardIcon';
import DashboardMenuIcon from '@/components/svg/DashboardMenuIcon';
import DashboardPercentageIcon from '@/components/svg/DashboardPercentageIcon';
import DashboardPowerIcon from '@/components/svg/DashboardPowerIcon';
import DashboardRefreshIcon from '@/components/svg/DashboardRefreshIcon';
import DashboardShopIcon from '@/components/svg/DashboardShopIcon';
import DashboardToolIcon from '@/components/svg/DashboardToolIcon';
import DashboardUserIcon from '@/components/svg/DashboardUserIcon';
import DashboardUserPlusIcon from '@/components/svg/DashboardUserPlusIcon';
import { useLogout } from '@/helpers/hooks/useLogout';
import { useManageBlogs } from '@/helpers/hooks/useManageBlogs';
import useWebAlias from '@/helpers/hooks/useWebalias';
import cn from '@/helpers/utils/classNames';
import { OpenUrlInNewTab } from '@/helpers/utils/common';
import { generateApproperiateURL } from '@/helpers/utils/urlHelpers';
import { useAppContext } from '@/layouts/AppProvider';
import { RootState, useAppSelector } from '@/store';
import { CustomLink } from '@/types/Common';
import { MetaDataStrapi } from '@/types/MetaData';

import SidebarItem from './components/SidebarItem';
import { useDashboardProvider } from './DashboardProvider';
import { SidebarProvider } from './DashboardSidebarProvider';

// eslint-disable-next-line sonarjs/cognitive-complexity
const Sidebar = () => {
  const { webalias, generateURL } = useWebAlias();
  const { isBackOffice } = useAppContext();
  const { toggleMenu } = useDashboardProvider();
  const { customer, isAdmin, customPages } = useAuthContext();
  const { handleLibraryClick } = useRoqLogicLibrary();
  const { handleManageBlogsClick } = useManageBlogs();

  const metaData: MetaDataStrapi = useAppSelector(
    (state: RootState) => state.metaDataApi.queries['getMetaData(undefined)']?.data as MetaDataStrapi
  );
  const isUserAuthorized = useAppSelector(store => store.slices.authenticationSlice.isAuthenticated);
  const signInRoute = generateURL('/account/login', webalias);
  const { logout } = useLogout(isBackOffice, webalias);

  const { Dog, Cat } = metaData || {};

  const catIssues = Cat?.Issues || [];
  const dogIssues = Dog?.Issues || [];

  const catCategories = (Cat?.Categories ? [...Cat.Categories] : []).sort((a, b) => {
    // Convert both names to lowercase for case-insensitive sorting
    const nameA = a?.Name?.toLowerCase();
    const nameB = b?.Name.toLowerCase();

    if (a.id === Infinity || b.id === Infinity) return 0;

    // Compare the names
    if (nameA < nameB) {
      return -1;
    }
    if (nameA > nameB) {
      return 1;
    }
    // Names are equal
    return 0;
  });

  const dogCategories = (Dog?.Categories ? [...Dog.Categories] : []).sort((a, b) => {
    // Convert both names to lowercase for case-insensitive sorting
    const nameA = a?.Name?.toLowerCase();
    const nameB = b?.Name.toLowerCase();

    // Compare the names

    if (a.id === Infinity || b.id === Infinity) return 0;

    if (nameA < nameB) {
      return -1;
    }
    if (nameA > nameB) {
      return 1;
    }
    // Names are equal
    return 0;
  });

  const ourStories = [
    {
      name: 'Pet Stories',
      link: isBackOffice
        ? `https://shop.pawtree.com/${customer?.webAlias}/testimonial`
        : generateURL('/testimonial', webalias),
    },
    {
      name: 'petPro Stories',
      link: isBackOffice
        ? `https://shop.pawtree.com/${customer?.webAlias}/testimonial/petpros`
        : generateURL('/testimonial/petpros', webalias),
    },
  ];

  const aboutUs = useMemo(() => {
    return [
      {
        name: 'Earn An Income',
        link: isBackOffice
          ? `https://shop.pawtree.com/${customer?.webAlias}/enrollment`
          : generateURL('/enrollment', webalias),
        as: 'a',
      },
      {
        name: 'pawTree Story',
        link: isBackOffice
          ? `https://shop.pawtree.com/${customer?.webAlias}/pawtree-story`
          : generateURL('/pawtree-story', webalias),
        as: 'a',
      },
      {
        name: 'Why pawTree',
        link: isBackOffice
          ? `https://shop.pawtree.com/${customer?.webAlias}/why-pawtree`
          : generateURL('/why-pawtree', webalias),
        as: 'a',
      },
      {
        name: 'Join Paw Club',
        link: isBackOffice
          ? `https://shop.pawtree.com/${customer?.webAlias}/paw-club`
          : generateURL('/paw-club', webalias),
        as: 'a',
      },
    ] as CustomLink[];
  }, [customer, generateURL, isBackOffice, webalias]);

  const businessReports = useMemo(
    () =>
      [
        { name: 'Pawtrip Report', link: generateURL('/pawtrip-report') },
        { name: 'Volume Reports', link: generateURL('/volume-reports') },
        { name: 'Downline Orders Report', link: generateURL('/downline-orders') },
        { name: 'Fast Start Report', link: generateURL('/faststart-Report') },
        { name: 'Former petPro Report', link: generateURL('/formerPetpro-Report') },
        { name: 'MVR Caps Report', link: generateURL('/MVR-Caps-Report') },
        { name: 'petPro Report', link: generateURL('/Petpro-Report') },
        { name: 'Prospect List', link: generateURL('/Prospect-List') },
        { name: 'Qualification History Report', link: generateURL('/Qualification-History-Report') },
        { name: 'Title Reports', link: generateURL('/title-reports') },
        { name: 'Tree Viewer', link: generateURL('/tree-viewer') },
      ] as CustomLink[],
    [generateURL]
  );

  const customerReports = useMemo(
    () =>
      [
        { name: 'Cancelled EZ Ship Report', link: generateURL('/Cancellation-Report') },
        { name: 'Customer List', link: generateURL('/Customer-List') },
        { name: 'Customer History', link: generateURL('/Customer-History') },
        { name: 'Pending EZ Ship Orders', link: generateURL('/pending-ezship-orders') },
        { name: 'Pet Quiz Report', link: generateURL('/organization/petprofilereport') },
        { name: 'Product Sales Report', link: generateURL('/Product-Sales-Report') },
        { name: 'Lead Generation', link: generateURL('/lead-generation-report') },

        // ...(customer && isPuptimusPrime(customer)
        //   ? [{ name: 'Abandoned Cart Report', link: generateURL('/abandoned-cart-report'), as: 'a' }]
        //   : []),
      ] as CustomLink[],
    [generateURL, customer]
  );

  const commissions = useMemo(
    () =>
      [
        { name: 'Commissions', link: generateURL('/commissions'), as: 'a' },
        { name: 'Monthly Commission Earnings', link: generateURL('/volumes'), as: 'a' },
        {
          name: 'PawPay',
          link: 'https://pawpay.mypayquicker.com',
          target: '_blank',
          // onClick: link => OpenUrlInNewTab(link),
          as: 'a',
        },
      ] as CustomLink[],
    [generateURL]
  );

  const events = useMemo(
    () =>
      [
        {
          name: 'Legacy',
          link: 'https://escadadesign.wixsite.com/pawtree-legacy-2026',
          target: '_blank',
          as: 'a',
          // onClick: link => OpenUrlInNewTab(link),
        },
        {
          name: 'PawFest',
          link: 'https://pawtreepawfest.com',
          target: '_blank',
          as: 'a',
          onClick: () => OpenUrlInNewTab('https://pawtreepawfest.com'),
        },
        {
          name: 'Pawtree Blog',
          link: `https://blog.pawtree.com/${
            isBackOffice ? customer?.webAlias || customer?.company || webalias : webalias
          }`,
          as: 'a',
          target: '_blank',
          // onClick: link => OpenUrlInNewTab(link),
        },
      ] as CustomLink[],
    [generateURL, customer, isBackOffice, webalias]
  );

  const proTools = useMemo(
    () =>
      [
        {
          name: 'Pawtree University',
          link: 'https://pawtreeuniversity.com',
          target: '_blank',
          as: 'a',
          // onClick: link => OpenUrlInNewTab(link),
        },
        { name: 'Library', link: '#', as: 'a', onClick: handleLibraryClick },
        { name: 'ProShop', link: generateURL('/shopping/products/557'), as: 'Link' },
        { name: 'Fund A Gift Card', link: generateURL('/giftcards/fundgiftcard'), as: 'a' },
        {
          name: 'Enroll a petPro',
          link: `https://shop.pawtree.com/${customer?.webAlias}/enrollment`,
          target: '_blank',
          as: 'a',
        },
      ] as CustomLink[],
    [generateURL, handleLibraryClick]
  );

  const support = useMemo(
    () =>
      [
        { name: 'Help Desk', link: 'mailto:support@pawtree.com', as: 'a' },
        {
          name: 'petPro FAQ',
          link: 'https://pawtree.zendesk.com/hc/en-us',
          target: '_blank',
          as: 'a',
          // onClick: link => OpenUrlInNewTab(link),
        },
        // {
        //   name: 'SOLUTIONS GUIDE FAQ',
        //   link: 'https://pawtree.zendesk.com/hc/en-us/articles/360049598733',
        //   target: '_blank',
        //  as: 'a',
        //   onClick: link => OpenUrlInNewTab(link),
        // },
      ] as CustomLink[],
    []
  );

  const tools = useMemo(
    () =>
      [
        { name: 'Manage Gift Cards', link: generateURL('/giftcards/managegiftcards') },
        { name: 'Pick A Name', link: generateURL('/randomizer') },
        {
          name: 'Manage Blogs',
          link: generateURL('/blogs'),
          as: 'a',
          onClick: (e: React.MouseEvent<HTMLAnchorElement | HTMLButtonElement, MouseEvent>) =>
            handleManageBlogsClick(e, generateURL('/blogs')),
        },
        {
          name: 'Manage Blog Images',
          link: generateURL('/blogs/blogimages'),
          as: 'a',
          onClick: (e: React.MouseEvent<HTMLAnchorElement | HTMLButtonElement, MouseEvent>) =>
            handleManageBlogsClick(e, generateURL('/blogs/blogimages')),
        },
        { name: 'Giftcard Transactions Report', link: generateURL('/giftcard-transactions-report') },
        { name: 'Funded Giftcard Report', link: generateURL('/funded-giftcard-transactions-report') },
        { name: 'Admin GC Transactions Report', link: generateURL('/admin-giftcard-transactions-report') },
        // { name: 'Manage Item Inventory', link: generateURL('/manage-item-inventory') },
        { name: 'Manage Breed', link: generateURL('/admin/managebreed') },
      ] as CustomLink[],
    [generateURL, customer, handleManageBlogsClick]
  );

  const petProfileAdmin = useMemo(
    () =>
      [
        { name: 'Manage Food Store', link: generateURL('/admin/managefoodstore'), as: 'a' },
        { name: 'Other Food', link: generateURL('/admin/manageotherfoods'), as: 'a' },
        { name: 'Manage Breed', link: generateURL('/admin/managebreed'), as: 'a' },
        { name: 'Recipe Recommendations', link: generateURL('/admin/reciperecommendation'), as: 'a' },
        { name: 'Pet Quiz', link: generateURL('/admin/petprofile'), as: 'a' },
        { name: 'Labels Report', link: generateURL('/petprofile/labelreport'), as: 'a' },
      ] as CustomLink[],
    [generateURL]
  );

  const myRecommendations = useMemo(() => {
    const customPagesLinks = (customPages || [])?.map(({ name, customPageID }) => ({
      link: `/customizedrecommendation/edit/${customPageID}`,
      name: name,
      as: 'a',
    }));

    if (customPagesLinks.length < 3) {
      return [
        ...customPagesLinks,
        { link: `/customizedrecommendation`, name: 'Create Custom Page', as: 'a' as 'a' },
      ] as CustomLink[];
    }

    return customPagesLinks as CustomLink[];
  }, [customPages]);

  const petProfilePath = isBackOffice
    ? `https://shop.pawtree.com/${customer?.webAlias ?? ''}/petprofile`
    : generateURL('/petprofile', webalias);
  const ezShipManagerPath = isBackOffice ? '/AutoOrders/auto-order-list' : '/auto-order/auto-order-list';

  const userSettings = useMemo(() => {
    const settings = [
      {
        name: 'Account Settings',
        link: isBackOffice ? '/account/settings' : generateURL('/account/settings', webalias),
      },
      { name: 'Orders', link: isBackOffice ? '/orders' : generateURL('/orders', webalias) },
      {
        name: 'EZ Ships',
        link: isBackOffice ? '/AutoOrders/auto-order-list' : generateURL('/auto-order/auto-order-list', webalias),
      },
      { name: 'Addresses', link: isBackOffice ? '/account/addresses' : generateURL('/account/addresses', webalias) },
    ] as CustomLink[];

    if (isBackOffice) {
      settings.push({ name: 'Avatar', link: generateURL('/account/avatar') });
      settings.push({ name: 'Point Accounts', link: generateURL('/account/earnings-account') });
      settings.push({ name: 'Notifications', link: generateURL('/account/notifications') });
    } else {
      settings.push({ name: 'Payment Methods', link: generateURL('/account/paymentmethods', webalias) });
      settings.push({ name: 'Fund A Gift Card', link: generateURL('/giftcards/fundgiftcard', webalias) });
    }

    // settings.push({ name: 'Manage Custom Food Labels', link: generateURL('/foodlabel/managelabels') });

    return settings;
  }, [generateURL, isBackOffice, webalias]);

  const dashboardUrl = '/';
  const blogUrl = `https://blog.pawtree.com/${isBackOffice ? customer?.webAlias || customer?.company || '' : webalias}`;
  const ingredientUrl = generateURL('/ingredient-glossary', webalias);
  return (
    <>
      <SidebarProvider>
        <div
          className={cn(
            'v2-relative md:v2-h-[90%] v2-h-[85%] v2-mb-6',
            toggleMenu ? 'v2-px-3' : 'v2-px-4 v2-overflow-hidden v2-overflow-y-auto custom-scroll'
          )}
        >
          <ul className="v2-flex v2-flex-col v2-gap-2">
            <SidebarItem
              item={{
                icon: <DashboardIcon />,
                name: 'Dashboard',
                link: dashboardUrl,
              }}
              depth={0}
            />
            <SidebarItem
              item={{
                icon: <DashboardMenuIcon />,
                name: 'Main Menu',
                subMenu: [
                  {
                    name: 'Shop Dogs',
                    subMenu: [
                      {
                        name: 'Shop By Products',
                        subMenu:
                          dogCategories?.map(category => ({
                            name: category.Name,
                            link: generateApproperiateURL(
                              `/products/${category.id === Infinity ? '' : category.slug || category.id}`,
                              webalias
                            ),
                            as: 'Link',
                          })) ?? [],
                      },
                      {
                        name: 'Shop By Issues',
                        subMenu:
                          dogIssues?.map(issue => ({
                            name: issue.Name,
                            link: generateApproperiateURL(
                              `/products/${issue.id === Infinity ? '' : issue.slug || issue.id}`,
                              webalias
                            ),
                          })) ?? [],
                      },
                    ],
                  },
                  {
                    name: 'Shop Cats',
                    subMenu: [
                      {
                        name: 'Shop By Products',
                        subMenu:
                          catCategories?.map(category => ({
                            name: category.Name,
                            link: generateApproperiateURL(
                              `/products/${category.id === Infinity ? '' : category.slug || category.id}`,
                              webalias
                            ),
                          })) ?? [],
                      },
                      {
                        name: 'Shop By Issues',
                        subMenu:
                          catIssues?.map(issue => ({
                            name: issue.Name,
                            link: generateApproperiateURL(
                              `/products/${issue.id === Infinity ? '' : issue.slug || issue.id}`,
                              webalias
                            ),
                          })) ?? [],
                      },
                    ],
                  },
                  { name: 'About Us', subMenu: aboutUs },
                  {
                    name: 'Resources',
                    subMenu: [
                      { name: 'Blog', link: blogUrl, as: 'a' },
                      { name: 'Ingredients 101', link: ingredientUrl, as: 'a' },
                    ],
                  },
                  { name: 'Pet Quiz', link: petProfilePath, as: 'a', target: isBackOffice ? '_blank' : undefined },
                ],
              }}
              depth={0}
            />
            {isUserAuthorized && (
              <SidebarItem
                item={{
                  icon: <DashboardShopIcon />,
                  name: 'My Recommendations',
                  subMenu: myRecommendations,
                }}
                depth={0}
              />
            )}
            <SidebarItem
              item={{
                icon: <DashboardFileIcon />,
                name: 'Reports',
                subMenu: [
                  { name: 'Business Reports', subMenu: businessReports },
                  {
                    name: 'Customer Reports',
                    subMenu: customerReports,
                  },
                ],
              }}
              depth={0}
            />
            <SidebarItem
              item={{
                icon: <DashboardPercentageIcon />,
                name: 'Commissions',
                subMenu: commissions,
              }}
              depth={0}
            />
            <SidebarItem
              item={{
                icon: <DashboardAnnouncementIcon />,
                name: 'Events',
                subMenu: events,
              }}
              depth={0}
            />
            {isAdmin && (
              <SidebarItem
                item={{
                  icon: <DashboardToolIcon />,
                  name: 'Tools',
                  subMenu: tools,
                }}
                depth={0}
              />
            )}
            <SidebarItem
              item={{
                icon: <DashboardUserPlusIcon />,
                name: 'Pro Tools',
                subMenu: proTools,
              }}
              depth={0}
            />
            <SidebarItem
              item={{
                icon: <DashboardHelpIcon />,
                name: 'Support',
                subMenu: support,
              }}
              depth={0}
            />
            <SidebarItem
              item={{
                icon: <DashboardUserIcon />,
                name: 'Account',
                subMenu: userSettings,
              }}
              depth={0}
            />
            {/* <SidebarItem
              item={{
                icon: <DashboardUserIcon />,
                name: 'Petprofile Admin',
                subMenu: petProfileAdmin,
              }}
              depth={0}
            /> */}
            <SidebarItem
              item={{
                icon: <DashboardRefreshIcon />,
                name: 'EZ Ship Manager',
                link: ezShipManagerPath,
                onClick: () => {
                  localStorage.setItem('active-ez-ship-manager', 'true');
                },
              }}
              depth={0}
            />
            <SidebarItem
              item={{
                icon: <DashboardPowerIcon />,
                name: 'Sign Out',
                link: '#',
                as: 'a',
                onClick: (e: React.MouseEvent<HTMLAnchorElement | HTMLButtonElement, MouseEvent>) => {
                  e.preventDefault();
                  if (isUserAuthorized) {
                    logout();
                  } else {
                    window.location.href = signInRoute;
                  }
                },
              }}
              depth={0}
            />
          </ul>
        </div>
      </SidebarProvider>
    </>
  );
};

export default Sidebar;
